<template>
  <div class="publication-type-step step-inner-padded" data-testid="oc-publication-type-step">
    <div class="card">
      <div class="card-body p-4">
        <h4>{{ $t('order_creation.publication_type.title') }}</h4>
        <p class="text-muted">{{ $t('order_creation.publication_type.subtitle') }}</p>
        <div class="wizard-option-grid publication-type-selector">
          <div
            class="wizard-option-card"
            data-testid="oc-publication-type-application"
            :class="{ 'is-active': !isFunnel }"
            @click="isFunnel = false">
            <div class="wizard-option-icon">
              <IconBrowserPlus size="24" />
            </div>
            <span class="wizard-option-title">{{ $t('order_creation.publication_type.online.title') }}</span>
            <span class="wizard-option-hint">{{ $t('order_creation.publication_type.online.hint') }}</span>
          </div>
          <div
            class="wizard-option-card"
            data-testid="oc-publication-type-funnel"
            :class="{ 'is-active': isFunnel }"
            @click="isFunnel = true">
            <div class="wizard-option-icon">
              <IconChartFunnel size="24" />
            </div>
            <span class="wizard-option-title">Funnel</span>
            <span class="wizard-option-hint">Lead Funnel</span>
          </div>
        </div>
        <h4>
          {{
            isFunnel
              ? $t('order_creation.publication_type.settings')
              : $t('order_creation.publication_type.content_settings')
          }}
        </h4>
        <p class="text-muted">
          {{
            isFunnel
              ? $t('order_creation.publication_type.settings_hint')
              : $t('order_creation.publication_type.content_settings_hint')
          }}
        </p>
        <div class="mb-3 d-flex align-items-start justify-content-between">
          <div class="d-flex align-items-start gap-1">
            <LiFormInput id="optimizeForFunnel" type="switch" v-model="optimizeContent" class="form-switch-lg ps-3" />
            <label class="w-75" for="optimizeForFunnel">
              <div class="switch-title">
                {{
                  isFunnel
                    ? $t('order_creation.publication_type.optimize_title')
                    : $t('order_creation.publication_type.optimize_content_title')
                }}
              </div>
              <div class="switch-subtitle">
                {{
                  isFunnel
                    ? $t('order_creation.publication_type.optimize_hint')
                    : $t('order_creation.publication_type.optimize_content_hint')
                }}
              </div>
            </label>
          </div>
          <div>
            <IconSparkles :size="24" color="currentColor" class="tw:text-primary" />
          </div>
        </div>
        <div class="d-flex align-items-start justify-content-between">
          <div class="d-flex align-items-start gap-1">
            <LiFormInput id="generateQuestions" type="switch" v-model="generateQuestions" class="form-switch-lg ps-3" />
            <label class="w-75" for="generateQuestions">
              <div class="switch-title">{{ $t('order_creation.publication_type.questions_title') }}</div>
              <div class="switch-subtitle">{{ $t('order_creation.publication_type.questions_hint') }}</div>
            </label>
          </div>
          <div>
            <IconSparkles :size="24" color="currentColor" class="tw:text-primary" />
          </div>
        </div>
        <hr class="section-divider" />
        <div class="kontakt">
          <UserEmailsSelector
            v-model="selectedContacts"
            :required="true"
            :initial-emails="order.application_receivers"
            :label="$t('order_creation.publication_type.contact.label')"
            :error="contactError"
            @change="handleSelectedContactsChange">
            <template #label>
              <h4>
                {{ $t('order_creation.publication_type.contact.title') }}
                <span class="required-indicator">*</span>
              </h4>
              <p class="text-muted">{{ $t('order_creation.publication_type.contact.subtitle') }}</p>
            </template>
          </UserEmailsSelector>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { useOrderCreationStore } from '@/stores/orderCreation';
import { IconBrowserPlus, IconChartFunnel, IconSparkles } from '@tabler/icons-vue';
import { FormInput as LiFormInput } from '@prospective/lithium';
import { storeToRefs } from 'pinia';
import UserEmailsSelector, { type ContactOption } from '@/components/user/UserEmailsSelector.vue';
import { computed, ref, watch } from 'vue';

const orderCreationStore = useOrderCreationStore();
const { order, isFunnel, shouldCreatePublication, generateQuestions, optimizeContent, stepErrors } =
  storeToRefs(orderCreationStore);

const allowNext = defineModel<boolean>('allowNext', { required: true });

const selectedContacts = ref<ContactOption[]>([]);
const contactError = computed(() => stepErrors.value.application_receivers?.[0] ?? null);

function handleSelectedContactsChange(contacts: ContactOption[]) {
  delete stepErrors.value.application_receivers;

  order.value.application_receivers = contacts.map((contact) => contact.email);
}

/* ---------------------------------- WATCH --------------------------------- */
watch([isFunnel, generateQuestions, optimizeContent], () => {
  shouldCreatePublication.value = true; // trigger regeneration of publication on any relevant setting change
});

watch(
  () => order.value.application_receivers,
  (receivers) => {
    allowNext.value = (receivers?.length ?? 0) > 0;
  },
  { immediate: true, deep: true }
);
</script>
<style scoped>
.publication-type-step {
  display: flex;
  justify-content: center;
}

.publication-type-step > .card {
  width: 600px;
}

.publication-type-selector {
  grid-template-columns: repeat(2, 1fr);
  margin-bottom: 20px;
}

.switch-title {
  font-weight: 600;
  font-size: 14px;
}

.switch-subtitle {
  font-size: 13px;
  color: #8e939d;
  font-weight: 400;
}

.section-divider {
  margin: 2rem -2rem;
}
</style>
